import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ContentCopy, Delete, Favorite, FavoriteBorder, Visibility } from '@mui/icons-material';
import Build, { SLOT_IDX } from '../../core/Build';
import { getSlotImg } from '../../core/buildUtils';
import { canonicalBuildPath, localGenPath } from '../../utils/buildRoutes';
import { formatCount } from '../../utils/formatCount';
import BuildCharacter from '../build/BuildCharacter';
import BuildTooltip from './BuildTooltip';
import Tooltip from '../utils/Tooltip';
import CriteriaTagStrip from '../generator/CriteriaTagStrip';
import {
    buildElementGradient,
    buildElementGradientHover,
    buildElementGradientActive,
    buildElementGradientActiveHover,
} from '../utils/CardGradient';
import { itemDisplayName } from '../../utils/myBuildsCardUtils';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import MediaImg from '../utils/MediaImg';
import './BuildCards.css';

const TXT_DUPLICATE = { fr: 'Dupliquer', en: 'Duplicate', de: 'Duplizieren', es: 'Duplicar', pt: 'Duplicar' };
const TXT_DELETE = { fr: 'Supprimer', en: 'Delete', de: 'Löschen', es: 'Eliminar', pt: 'Excluir' };
const TXT_UNNAMED = { fr: 'Sans nom', en: 'Unnamed', de: 'Ohne Namen', es: 'Sin nombre', pt: 'Sem nome' };
const TXT_ACTIVE = { fr: 'Actif', en: 'Active', de: 'Aktiv', es: 'Activo', pt: 'Ativo' };

const DEFAULT_BUILD_LEVEL = 200;
const DATE_DISPLAY_OPTIONS = { day: '2-digit', month: 'short', year: 'numeric' };

function formatDate(iso) {
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, DATE_DISPLAY_OPTIONS);
    } catch {
        return '';
    }
}


function BuildCardSlot({ item, icon, slotId, lang }) {
    const alt = item ? (itemDisplayName(item, lang) || '') : '';
    const src = item?.icon || icon;
    return (
        <div className="mybuilds-slot">
            {src ? (
                <MediaImg
                    className="mybuilds-slot-img"
                    src={resolveMediaUrl(src)}
                    alt={alt}
                    loading="lazy"
                    fill
                    resolve={false}
                />
            ) : (
                <img className="mybuilds-slot-img empty" src={getSlotImg(slotId)} alt={alt} loading="lazy" />
            )}
        </div>
    );
}

/**
 * The gear block.
 *
 * `slotIcons` is the community-listing form: a card there arrives with icon
 * paths instead of items (see the backend's CARD_ICONS_KEY), so the slot falls
 * back to the path when the build itself carries no equipment.
 */
export function BuildCardBuildPreview({ buildModel, lang, slotIcons = null, gearBlockOverlay = null }) {
    const b = buildModel;
    const iconAt = (slotId) => (Array.isArray(slotIcons) ? slotIcons[slotId] || null : null);
    return (
        <div className="mybuilds-card-build">
            <div className="mybuilds-card-gear-block">
                <div className="mybuilds-card-character-layer" aria-hidden="true">
                    <BuildCharacter build={b} enableSkinRender={false} />
                </div>
                <div className="mybuilds-card-slot-rows">
                    <div className="mybuilds-card-slot-col">
                        <BuildCardSlot item={b.getAmulet()} icon={iconAt(SLOT_IDX.AMULET)} slotId={SLOT_IDX.AMULET} lang={lang} />
                        <BuildCardSlot item={b.getShield()} icon={iconAt(SLOT_IDX.SHIELD)} slotId={SLOT_IDX.SHIELD} lang={lang} />
                        <BuildCardSlot item={b.getRingL()} icon={iconAt(SLOT_IDX.RING1)} slotId={SLOT_IDX.RING1} lang={lang} />
                        <BuildCardSlot item={b.getBelt()} icon={iconAt(SLOT_IDX.BELT)} slotId={SLOT_IDX.BELT} lang={lang} />
                        <BuildCardSlot item={b.getBoots()} icon={iconAt(SLOT_IDX.BOOTS)} slotId={SLOT_IDX.BOOTS} lang={lang} />
                    </div>
                    <div className="mybuilds-card-slot-col mybuilds-card-slot-col--right">
                        <BuildCardSlot item={b.getHat()} icon={iconAt(SLOT_IDX.HAT)} slotId={SLOT_IDX.HAT} lang={lang} />
                        <BuildCardSlot item={b.getWeapon()} icon={iconAt(SLOT_IDX.WEAPON)} slotId={SLOT_IDX.WEAPON} lang={lang} />
                        <BuildCardSlot item={b.getRingR()} icon={iconAt(SLOT_IDX.RING2)} slotId={SLOT_IDX.RING2} lang={lang} />
                        <BuildCardSlot item={b.getCloak()} icon={iconAt(SLOT_IDX.CLOAK)} slotId={SLOT_IDX.CLOAK} lang={lang} />
                        <BuildCardSlot item={b.getPet()} icon={iconAt(SLOT_IDX.PET)} slotId={SLOT_IDX.PET} lang={lang} />
                    </div>
                </div>
                {gearBlockOverlay}
            </div>
            <div className="mybuilds-card-trophies" aria-hidden="true">
                {b.getTrophus().map((item, index) => (
                    <BuildCardSlot
                        key={`t${SLOT_IDX.TROPHUS[index]}`}
                        item={item}
                        icon={iconAt(SLOT_IDX.TROPHUS[index])}
                        slotId={SLOT_IDX.TROPHUS[index]}
                        lang={lang}
                    />
                ))}
            </div>
        </div>
    );
}

function entryHref(entry, lang) {
    const slug = entry?.owner?.public_slug;
    const code = entry?.publicCode;
    if (slug && code) return canonicalBuildPath(lang, slug, code);
    return localGenPath(lang);
}

function isPlainLeftClick(e) {
    return e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
}

/**
 * Shared library / liked build card shell.
 * Stretch link opens the build; footer controls sit above it.
 * Optional onActivate runs on plain left-click (e.g. openBuild) instead of default nav.
 */
export function BaseBuildCard({
    entry,
    isActive = false,
    lang,
    to,
    onActivate,
    footer,
    activeLabel,
    activeTitle,
}) {
    const raw = entry?.build;
    const lvl = raw?.lvl ?? DEFAULT_BUILD_LEVEL;
    const hasName = !!entry?.name?.trim();
    const label = hasName ? entry.name.trim() : activeTitle;
    const href = to || entryHref(entry, lang);
    const buildModel = useMemo(() => new Build(raw || {}), [raw]);
    const cls = buildModel.getCharacterClass?.();
    const classIcon = cls?.icon || '';
    const className = cls?.label?.[lang] || cls?.label?.en || cls?.name || '';
    const topGradVars = useMemo(() => {
        // getElementTint, not getStats: a listing card carries no equipment, and
        // the tint stored with the build is what still says which element it is.
        // With items present the two agree — the tint is derived from the stats.
        const color = buildModel.getElementTint();
        if (isActive) {
            return {
                '--mybuilds-card-grad': buildElementGradientActive(color),
                '--mybuilds-card-grad-hover': buildElementGradientActiveHover(color),
            };
        }
        return {
            '--mybuilds-card-grad': buildElementGradient(color),
            '--mybuilds-card-grad-hover': buildElementGradientHover(color),
        };
    }, [buildModel, isActive]);

    const onLinkClick = (e) => {
        if (!onActivate || !isPlainLeftClick(e)) return;
        e.preventDefault();
        onActivate();
    };

    return (
        <BuildTooltip
            build={buildModel}
            lang={lang}
            genConfig={entry?.genConfig}
            // Lets the panel fetch the gear the listing deliberately omitted.
            entry={entry}
            variant="stats"
        >
            <div
                className={`mybuilds-card ${isActive ? 'mybuilds-card-active' : ''}`}
                style={topGradVars}
            >
                <Link
                    to={href}
                    className="mybuilds-card-link"
                    aria-label={label}
                    onClick={onLinkClick}
                />
                <div className="mybuilds-card-gradient">
                    <div className="mybuilds-card-fill">
                        <header className="mybuilds-card-header">
                            <div className="mybuilds-card-header-text">
                                <h3 className={`mybuilds-card-name${hasName ? '' : ' mybuilds-card-name--unnamed'}`}>{label}</h3>
                                <div className="mybuilds-card-meta-row">
                                    <span className="mybuilds-card-lvl">
                                        {classIcon ? (
                                            <img
                                                src={classIcon}
                                                alt=""
                                                className="mybuilds-card-class-icon"
                                                title={className}
                                                draggable={false}
                                            />
                                        ) : null}
                                        <span>{lvl}</span>
                                    </span>
                                    <span className="mybuilds-card-meta-sep" aria-hidden="true">·</span>
                                    <span className="mybuilds-card-date">{formatDate(entry.updatedAt || entry.createdAt)}</span>
                                </div>
                                <div className="mybuilds-card-tags">
                                    {raw?.criteriaTags && raw.criteriaTags.length > 0 && (
                                        <CriteriaTagStrip tags={raw.criteriaTags} lang={lang} maxIcons={8} />
                                    )}
                                </div>
                            </div>
                        </header>
                        <div className="mybuilds-card-stage">
                            <BuildCardBuildPreview
                                buildModel={buildModel}
                                lang={lang}
                                slotIcons={raw?.itemIcons}
                                gearBlockOverlay={
                                    isActive && activeLabel ? (
                                        <div className="mybuilds-card-active-badge" role="status">
                                            <span className="mybuilds-badge mybuilds-badge--stage-active">{activeLabel}</span>
                                        </div>
                                    ) : null
                                }
                            />
                        </div>
                        <footer className="mybuilds-card-footer">
                            {footer}
                        </footer>
                    </div>
                </div>
            </div>
        </BuildTooltip>
    );
}

export function LibraryBuildCard({ entry, isActive, lang, onActivate, onDelete, onDuplicate, onToggleLike }) {
    const txtDuplicate = TXT_DUPLICATE[lang] || TXT_DUPLICATE.en;
    const txtDelete = TXT_DELETE[lang] || TXT_DELETE.en;
    const txtUnnamed = TXT_UNNAMED[lang] || TXT_UNNAMED.en;
    const txtActive = TXT_ACTIVE[lang] || TXT_ACTIVE.en;
    const likes = formatCount(entry?.metrics?.likes, lang);
    const views = formatCount(entry?.metrics?.views, lang);
    const hasLiked = !!entry?.viewer?.hasLiked;

    return (
        <BaseBuildCard
            entry={entry}
            isActive={isActive}
            lang={lang}
            onActivate={onActivate}
            activeLabel={txtActive}
            activeTitle={txtUnnamed}
            footer={(
                <div className="community-card-footer-row library-card-footer-row">
                    <div className="community-card-metrics library-card-footer-metrics">
                        <span className="community-views"><Visibility fontSize="small" /> <span>{views}</span></span>
                        {onToggleLike ? (
                            <button
                                type="button"
                                className={`mybuilds-btn community-like-btn ${hasLiked ? 'active' : ''}`}
                                onClick={() => onToggleLike(entry.id, hasLiked)}
                            >
                                {hasLiked ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                                <span>{likes}</span>
                            </button>
                        ) : (
                            <span className="community-views">
                                <FavoriteBorder fontSize="small" />
                                <span>{likes}</span>
                            </span>
                        )}
                        <Tooltip content={txtDuplicate}>
                            <button
                                type="button"
                                className="mybuilds-btn"
                                aria-label={txtDuplicate}
                                onClick={() => onDuplicate(entry.id)}
                            >
                                <ContentCopy fontSize="small" />
                            </button>
                        </Tooltip>
                        <Tooltip content={txtDelete}>
                            <button
                                type="button"
                                className="mybuilds-btn mybuilds-btn-danger"
                                aria-label={txtDelete}
                                onClick={() => onDelete(entry.id)}
                            >
                                <Delete fontSize="small" />
                            </button>
                        </Tooltip>
                    </div>
                </div>
            )}
        />
    );
}

export function CommunityBuildCard({ entry, lang, onToggleLike, hideOwner = false }) {
    const likes = formatCount(entry?.metrics?.likes, lang);
    const views = formatCount(entry?.metrics?.views, lang);
    const hasLiked = !!entry?.viewer?.hasLiked;
    const ownerName = entry?.owner?.display_name || entry?.owner?.public_slug || 'Unknown';
    const ownerSlug = entry?.owner?.public_slug;
    const ownerBlock = ownerSlug ? (
        <Link
            to={`/${lang}/community/${encodeURIComponent(ownerSlug)}`}
            className="community-owner community-owner-link"
        >
            {entry?.owner?.avatar_url ? <img src={entry.owner.avatar_url} alt="" className="community-owner-avatar" /> : null}
            <span className="community-owner-name">{ownerName}</span>
        </Link>
    ) : (
        <div className="community-owner">
            {entry?.owner?.avatar_url ? <img src={entry.owner.avatar_url} alt="" className="community-owner-avatar" /> : null}
            <span className="community-owner-name">{ownerName}</span>
        </div>
    );
    return (
        <BaseBuildCard
            entry={entry}
            lang={lang}
            activeTitle={TXT_UNNAMED[lang] || TXT_UNNAMED.en}
            activeLabel=""
            footer={(
                <div className="community-card-footer-row">
                    {hideOwner ? null : ownerBlock}
                    <div className="community-card-metrics">
                        <button
                            type="button"
                            className={`mybuilds-btn community-like-btn ${hasLiked ? 'active' : ''}`}
                            onClick={() => onToggleLike?.(entry.id, hasLiked)}
                        >
                            {hasLiked ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                            <span>{likes}</span>
                        </button>
                        <span className="community-views"><Visibility fontSize="small" /> <span>{views}</span></span>
                    </div>
                </div>
            )}
        />
    );
}

